// Single list-view row for a hosted site — composes SiteActions (desktop+mobile) and SiteDisclosure

import { FileCode, ChevronDown } from 'lucide-react'
import { cn } from '../../lib/utils'
import { formatBytes, formatDate, sitePublicUrl } from './utils'
import { SiteActions } from './site-actions'
import { SiteDisclosure } from './site-disclosure'
import type { HostedSite } from '../../hooks/use-hosted-sites'

export interface HostedSiteRowProps {
  site: HostedSite
  isDark: boolean
  expandedId: string | null
  toggleExpanded: (id: string) => void
  editingId: string | null
  draft: string
  setDraft: (v: string) => void
  summaryFull: boolean
  setSummaryFull: (fn: (v: boolean) => boolean) => void
  pendingSummaryId: string | null
  summaryError: { id: string; message: string } | null
  copiedId: string | null
  previewSite: HostedSite | null
  onCopyLink: (site: HostedSite) => void
  onTogglePreview: (site: HostedSite) => void
  onDelete: (site: HostedSite) => void
  onStartEdit: (site: HostedSite) => void
  onCancelEdit: () => void
  onCommitEdit: (site: HostedSite) => void
  onRegenerate: (id: string) => void
}

export function HostedSiteRow({
  site,
  isDark,
  expandedId,
  toggleExpanded,
  editingId,
  draft,
  setDraft,
  summaryFull,
  setSummaryFull,
  pendingSummaryId,
  summaryError,
  copiedId,
  previewSite,
  onCopyLink,
  onTogglePreview,
  onDelete,
  onStartEdit,
  onCancelEdit,
  onCommitEdit,
  onRegenerate,
}: HostedSiteRowProps) {
  const publicUrl = sitePublicUrl(site)
  const isExpanded = expandedId === site.id

  return (
    <div
      className={cn(
        'group rounded-lg border p-3 transition-colors',
        isDark
          ? 'border-white/[0.06] bg-surface-1 hover:bg-surface-2'
          : 'border-neutral-200 bg-white hover:bg-neutral-50',
      )}
    >
      {/* Top row: icon + info + desktop actions */}
      <div className="flex items-center gap-3">
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', isDark ? 'bg-surface-3' : 'bg-neutral-100')}>
          <FileCode className={cn('h-4 w-4', site.status === 'ready' ? (isDark ? 'text-brand-400' : 'text-brand-600') : 'text-neutral-400')} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={cn('truncate text-sm font-medium', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
              {site.filename}
            </p>
            <span className={cn(
              'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
              site.status === 'ready'
                ? 'bg-green-500/10 text-green-500'
                : site.status === 'failed'
                  ? 'bg-red-500/10 text-red-500'
                  : 'bg-yellow-500/10 text-yellow-500',
            )}>
              {site.status}
            </span>
          </div>
          <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
            <span>{formatBytes(site.totalSizeBytes)}</span>
            <span className="hidden sm:inline">·</span>
            <span>{site.fileCount} {site.fileCount === 1 ? 'file' : 'files'}</span>
            <span className="hidden sm:inline">·</span>
            <span>{formatDate(site.createdAt)}</span>
            <button
              type="button"
              onClick={() => toggleExpanded(site.id)}
              className={cn(
                'ml-auto flex items-center gap-1 rounded px-1 py-0.5 text-[11px]',
                isDark ? 'hover:bg-surface-3 text-neutral-400 hover:text-neutral-200' : 'hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700',
              )}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Hide details' : 'Show details'}
            >
              <span>{isExpanded ? 'Hide' : 'Details'}</span>
              <ChevronDown
                className={cn('h-3 w-3 transition-transform', isExpanded && 'rotate-180')}
              />
            </button>
          </div>
        </div>
        <SiteActions
          site={site}
          isDark={isDark}
          copiedId={copiedId}
          previewSite={previewSite}
          publicUrl={publicUrl}
          onCopyLink={onCopyLink}
          onTogglePreview={onTogglePreview}
          onDelete={onDelete}
          variant="desktop"
        />
      </div>

      {/* Disclosure panel — description (editable) + AI summary */}
      {isExpanded && (
        <SiteDisclosure
          site={site}
          isDark={isDark}
          isEditing={editingId === site.id}
          draft={draft}
          setDraft={setDraft}
          summaryFull={summaryFull}
          setSummaryFull={setSummaryFull}
          pendingSummaryId={pendingSummaryId}
          summaryError={summaryError}
          onStartEdit={onStartEdit}
          onCancelEdit={onCancelEdit}
          onCommitEdit={onCommitEdit}
          onRegenerate={onRegenerate}
        />
      )}

      {/* Mobile actions — always visible, full-width row */}
      <SiteActions
        site={site}
        isDark={isDark}
        copiedId={copiedId}
        previewSite={previewSite}
        publicUrl={publicUrl}
        onCopyLink={onCopyLink}
        onTogglePreview={onTogglePreview}
        onDelete={onDelete}
        variant="mobile"
      />
    </div>
  )
}
